import React, { useEffect, useState } from "react";
import RoleChip from "components/RoleChip";
import AdminCard from "components/AdminCard";
import { API_BASE_URL } from "config/api";
import { authApi } from "../auth/authApi";
import { useAuth } from "../auth/AuthContext";
import "../styles/admin-theme.css";

const LANGUAGES = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "uk", label: "Українська", flag: "🇺🇦" },
    { code: "tr", label: "Türkçe", flag: "🇹🇷" }
];

export default function UserProfilePage() {
    const {
        userName,
        roles,
        preferredLanguage,
        authFetch,
        refreshProfile,
        updatePreferredLanguage
    } = useAuth();

    const hasTradingRole = roles.includes("Trading.View");

    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [languageLoading, setLanguageLoading] = useState(false);
    const [languageMessage, setLanguageMessage] = useState("");
    const [languageError, setLanguageError] = useState("");

    const [roleRequestLoading, setRoleRequestLoading] = useState(false);
    const [roleRequestMessage, setRoleRequestMessage] = useState("");
    const [roleRequestError, setRoleRequestError] = useState("");

    useEffect(() => {
        refreshProfile().catch((error) => {
            console.error("Profil konnte nicht aktualisiert werden:", error);
        });
    }, []); // absichtlich nur beim Öffnen der Profilseite

    const changePassword = async () => {
        setSuccessMessage("");
        setErrorMessage("");

        try {
            const res = await authFetch(
                `${API_BASE_URL}/api/auth/change-password`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ oldPassword, newPassword })
                }
            );

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(text || "Passwort konnte nicht geändert werden");
            }

            setSuccessMessage("Passwort geändert");
            setOldPassword("");
            setNewPassword("");
        } catch (err) {
            setErrorMessage(
                err?.message || "Passwort konnte nicht geändert werden"
            );
        }
    };

    const changeLanguage = async (languageCode) => {
        if (languageLoading || languageCode === preferredLanguage) return;

        setLanguageLoading(true);
        setLanguageMessage("");
        setLanguageError("");

        try {
            const savedLanguage = await updatePreferredLanguage(languageCode);
            const selected = LANGUAGES.find(
                (language) => language.code === savedLanguage
            );

            setLanguageMessage(
                `Sprache wurde auf ${selected?.label || savedLanguage} geändert.`
            );
        } catch (err) {
            setLanguageError(
                err?.message || "Sprache konnte nicht gespeichert werden."
            );
        } finally {
            setLanguageLoading(false);
        }
    };

    const requestTradingRole = async () => {
        setRoleRequestLoading(true);
        setRoleRequestMessage("");
        setRoleRequestError("");

        try {
            const result = await authApi.requestRole("Trading.View");
            setRoleRequestMessage(result || "Antrag wurde gestellt.");
        } catch (err) {
            setRoleRequestError(
                err?.message || "Antrag konnte nicht gestellt werden."
            );
        } finally {
            setRoleRequestLoading(false);
        }
    };

    return (
        <div className="admin-theme">
            <AdminCard title="Mein Profil" subtitle={userName}>
                <h3 style={{ fontFamily: "Arial", color: "black" }}>
                    Sprache
                </h3>

                <p>
                    Die ausgewählte Sprache wird in deinem Profil gespeichert und
                    bei der nächsten Anmeldung automatisch wiederhergestellt.
                </p>

                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        marginBottom: 12
                    }}
                >
                    {LANGUAGES.map((language) => {
                        const selected =
                            preferredLanguage === language.code;

                        return (
                            <button
                                key={language.code}
                                type="button"
                                title={language.label}
                                aria-label={language.label}
                                aria-pressed={selected}
                                disabled={languageLoading}
                                onClick={() => changeLanguage(language.code)}
                                style={{
                                    width: 48,
                                    minWidth: 48,
                                    padding: "8px 6px",
                                    borderRadius: 8,
                                    border: selected
                                        ? "2px solid #0f766e"
                                        : "1px solid #b6c2c9",
                                    background: selected
                                        ? "rgba(15,118,110,0.12)"
                                        : "#fff",
                                    fontSize: 24,
                                    cursor: languageLoading
                                        ? "wait"
                                        : "pointer"
                                }}
                            >
                                {language.flag}
                            </button>
                        );
                    })}
                </div>

                {languageMessage && (
                    <div className="admin-msg success">{languageMessage}</div>
                )}

                {languageError && (
                    <div className="admin-msg error">{languageError}</div>
                )}

                <h3 style={{ fontFamily: "Arial", color: "black", marginTop: 28 }}>
                    Rollen
                </h3>

                <div style={{ marginBottom: 24 }}>
                    {roles.map((role) => (
                        <RoleChip key={role} label={role} />
                    ))}
                </div>

                {!hasTradingRole && (
                    <div style={{ marginBottom: 30 }}>
                        <h4
                            style={{
                                fontFamily: "var(--font-sans)",
                                color: "#0f766e"
                            }}
                        >
                            Trading.View beantragen
                        </h4>

                        <p style={{ marginTop: 0 }}>
                            Du hast aktuell keinen Zugriff auf Trading View.
                        </p>

                        <button
                            style={{ width: "200px" }}
                            onClick={requestTradingRole}
                            disabled={roleRequestLoading}
                        >
                            {roleRequestLoading
                                ? "Wird beantragt ..."
                                : "Trading.View beantragen"}
                        </button>

                        {roleRequestMessage && (
                            <div
                                className="admin-msg success"
                                style={{ marginTop: 10 }}
                            >
                                {roleRequestMessage}
                            </div>
                        )}

                        {roleRequestError && (
                            <div
                                className="admin-msg error"
                                style={{ marginTop: 10 }}
                            >
                                {roleRequestError}
                            </div>
                        )}
                    </div>
                )}

                <h4
                    style={{
                        fontFamily: "var(--font-sans)",
                        color: "#0f766e"
                    }}
                >
                    Passwort ändern
                </h4>

                <div style={{ display: "grid", gap: 10 }}>
                    <input
                        style={{ width: "200px" }}
                        type="password"
                        placeholder="Altes Passwort"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                    />

                    <input
                        style={{ width: "200px" }}
                        type="password"
                        placeholder="Neues Passwort"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />

                    <button
                        style={{ width: "200px" }}
                        onClick={changePassword}
                    >
                        Passwort ändern
                    </button>

                    {successMessage && (
                        <div className="admin-msg success">
                            {successMessage}
                        </div>
                    )}

                    {errorMessage && (
                        <div className="admin-msg error">
                            {errorMessage}
                        </div>
                    )}
                </div>
            </AdminCard>
        </div>
    );
}
